import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DropdownOption } from "@adsk/acc-issues-common/types/dropdown";
import EntityPickerModal from "./utils/EntityPickerModal";
import { useTranslations } from "@adsk/acc-issues-translations";
import PagePopover from "../../utils/components/PagePopover";
import { useIssuePlacement } from "@adsk/acc-issues-core/providers/IssuePlacementContext";
import { useCurrentProjectId } from "@adsk/alloy-provider";
import { processEntities } from "./utils/processEntities";
import {
  IssueLinkedDocument,
  IssuePlacementWithPins,
  IssueType,
  PlacementType,
} from "@adsk/acc-issues-types";
import { usePlacementFlow } from "./usePlacementFlow";
import {
  EntityPickerModalEntity,
  PlacementEntityType,
  PlacementFile,
  PlacementFlowStatus,
  PlacementSheet,
} from "./types";
import { EntityDisplay } from "./EntityDisplay";
import { forbiddenExtensions } from "./forbiddenExtensions";
import styled from "styled-components";
import { extensionsToFilter } from "./extensionsToFilter";
import { Control, DropdownMenu, useDropdown } from "@adsk/alloy-react-dropdown";
import { PlacementDropdownMenuItem } from "./PlacementDropdownMenuUtils";
import { useIsBuildAvailable } from "@adsk/acc-issues-common/hooks/useIsBuildAvailable";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { transformLinkedDocumentsToPlacements } from "@adsk/acc-issues-common/utils/data/linkedDocumentsToPlacements";
import { EntityItem, SheetItem } from "@adsk/docs-js-sdk";
import {
  IssuePlacementPinUpsert,
  SerializedContext,
} from "../../utils/types/PlacementViewer";
import { useGenerateIssuePlacements } from "../../hooks/useGenerateIssuePlacements";
import { getPlacementType } from "../../utils/data/getPlacementType";
import isEmpty from "lodash/isEmpty";

const forbiddenRootFolderName = "For the Field";

export type PlacementOptions = {
  locationId?: string | null;
  snapshotBlob?: Blob;
};

type PlacementDropdownPropTypes = {
  onChange?: (
    placements: IssuePlacementWithPins[],
    options: PlacementOptions,
    type?: PlacementType
  ) => void;
  defaultIsOpen?: boolean;
  onClose?: () => void;
  error?: boolean;
  issueType?: IssueType;
};

type MessageEventData =
  | { actionType: "close-viewer" }
  | {
      actionType: "confirm-stamp";
      locationId?: string | null;
      snapshotBlob: Blob;
      context: SerializedContext;
      pins: IssuePlacementPinUpsert[];
    }
  | {
      actionType: "confirm-pushpin";
      pushpin: IssueLinkedDocument[];
      snapshotBlob: Blob;
    };

const PlacementDropdownContainer = styled.div`
  width: 100%;
`;

export const PlacementDropdown = ({
  onChange,
  defaultIsOpen,
  onClose,
  error,
  issueType,
}: PlacementDropdownPropTypes) => {
  const {
    status,
    selectEntityType,
    selectEntity,
    associateEntity,
    resetToInitial,
    selectedEntityTypeConfig,
    entityType,
    entity,
    dropdownOptions,
    optionRenderer,
  } = usePlacementFlow();
  const { T } = useTranslations();
  const isBuildAvailable = useIsBuildAvailable();
  const generateIssuePlacements = useGenerateIssuePlacements();

  const disabledTooltip = T.translate(
    "issueCreation.placement.forTheField.cantSelect3DFiles"
  ) as string;

  const [channelInitiated, setChannelInitiated] = useState(false);
  const [pageLoadTimeoutReached, setPageLoadTimeoutReached] = useState(false);

  const resetState = useCallback(() => {
    resetToInitial();
    setChannelInitiated(false);
    setPageLoadTimeoutReached(false);
  }, [resetToInitial]);

  const handleBrowserBackButtonClicked = useCallback(() => {
    if (status === PlacementFlowStatus.ENTITY_SELECTED) {
      resetState();
    }
  }, [status, resetState]);

  const showEntityPicker = status === PlacementFlowStatus.ENTITY_TYPE_SELECTED;
  const getPlacementLink = useIssuePlacement();
  const projectId = useCurrentProjectId() ?? "";
  const { value: showThumbNailOf2dSnapshots } = useFeatureFlag(
    FeatureFlag.IssueThumbnailOf2dSnapshots
  );

  const viewerPageLink = useMemo(
    () =>
      !!selectedEntityTypeConfig && !!getPlacementLink
        ? getPlacementLink({
            projectId,
            document:
              selectedEntityTypeConfig.plugin.toolId === "documents"
                ? (entity as EntityItem)
                : undefined,
            sheet:
              selectedEntityTypeConfig.plugin.toolId ===
              PlacementEntityType.SHEETS
                ? (entity as SheetItem)
                : undefined,
            issueId: "",
            isIframe: true,
          })
        : "",
    [getPlacementLink, projectId, entity, selectedEntityTypeConfig]
  );

  const [isCloseAttempt, setIsCloseAttempt] = useState(false);

  const handleClose = useCallback(() => {
    setIsCloseAttempt(true);
  }, []);

  useEffect(() => {
    if (
      isCloseAttempt &&
      !showEntityPicker &&
      status === PlacementFlowStatus.NOTHING_SELECTED
    ) {
      onClose?.();
    }
  }, [onClose, isCloseAttempt, showEntityPicker, status]);

  const handleOptionChange = useCallback(
    (option: DropdownOption<PlacementEntityType>) => {
      selectEntityType(option.value);
    },
    [selectEntityType]
  );

  const handleChannelInit = useCallback(
    (_: MessageEvent, port2: MessagePort) => {
      if (issueType) {
        port2.postMessage(issueType);
      }
      setChannelInitiated(true);
    },
    [issueType]
  );

  useEffect(() => {
    if (status === PlacementFlowStatus.ENTITY_SELECTED) {
      setTimeout(() => {
        setPageLoadTimeoutReached(true);
      }, 5000);
    }
  }, [status]);

  const handleMessage = useCallback(
    (event: MessageEvent<MessageEventData>) => {
      if (typeof event.data !== "object") {
        return;
      }

      const { actionType } = event.data;

      switch (actionType) {
        case "close-viewer": {
          resetState();
          break;
        }
        case "confirm-stamp": {
          const { pins, locationId, snapshotBlob, context } = event.data;
          const placements = generateIssuePlacements(context, pins);
          const placementType = getPlacementType(pins[0], context);

          if (!isEmpty(placements)) {
            associateEntity();
          } else {
            resetState();
          }
          onChange?.(
            placements,
            {
              locationId,
              ...(showThumbNailOf2dSnapshots && { snapshotBlob }),
            },
            placementType
          );
          break;
        }
        case "confirm-pushpin": {
          const { pushpin, snapshotBlob } = event.data;
          if (pushpin) {
            associateEntity();
          } else {
            resetState();
          }
          onChange?.(
            transformLinkedDocumentsToPlacements(pushpin),
            { snapshotBlob },
            PlacementType.ThreeDFileView
          );
          break;
        }
        default:
          return;
      }
      setTimeout(() => onClose?.());
    },
    [
      resetState,
      generateIssuePlacements,
      onChange,
      showThumbNailOf2dSnapshots,
      associateEntity,
      onClose,
    ]
  );

  const dataViewPropsObj = useMemo(() => {
    return {
      dataFilter: (entities: EntityPickerModalEntity[]) =>
        processEntities({
          entities,
          disabledTooltip,
          forbiddenRootFolderName,
          forbiddenExtensions,
          extensionsToFilter,
        }),
    };
  }, [disabledTooltip]);

  const { controlProps, menuProps, closeMenu } = useDropdown({
    items: dropdownOptions,
    selectedItem: null,
    defaultIsOpen,
    onSelectedItemChange: ({ selectedItem }) => {
      handleOptionChange(selectedItem!);
      closeMenu();
    },
    onIsOpenChange: handleClose,
    id: "placement-dropdown",
    isItemDisabled: ({ value }) => {
      if (value === PlacementEntityType.SHEETS) {
        return !isBuildAvailable;
      }
      return false;
    },
  });

  const PlacementDropdown = (
    <div style={{ width: "100%" }}>
      <Control
        {...controlProps}
        renderItem={({ selectedItem }) => optionRenderer(selectedItem!)}
        data-testid={"dropdown-control"}
      />
      <DropdownMenu
        {...menuProps}
        renderMenuItem={({ item, ...props }) => (
          <PlacementDropdownMenuItem
            option={item}
            props={props}
            key={item.value}
          />
        )}
        renderFooter={() => null}
        data-testid={"dropdown-menu"}
      />
    </div>
  );

  return (
    <PlacementDropdownContainer data-testid="PlacementDropdown">
      {status === PlacementFlowStatus.ENTITY_ASSOCIATED ? (
        <EntityDisplay
          entityType={entityType}
          entity={entity}
          error={error}
          onRemove={() => {
            onChange?.([], {});
            resetToInitial();
          }}
        />
      ) : (
        PlacementDropdown
      )}
      <EntityPickerModal
        open={showEntityPicker}
        plugin={selectedEntityTypeConfig?.plugin}
        closeModal={resetToInitial}
        onSave={(entity: PlacementFile | PlacementSheet) => {
          const entityToSelect = {
            ...entity,
            ...(entityType === PlacementEntityType.SHEETS && {
              id: (entity as PlacementSheet & { sheetId: string }).sheetId,
            }),
          };
          selectEntity(entityToSelect);
        }}
        title={selectedEntityTypeConfig?.panelTitle}
        dataViewProps={dataViewPropsObj}
      />
      <PagePopover
        src={viewerPageLink}
        open={status === PlacementFlowStatus.ENTITY_SELECTED}
        show={channelInitiated || pageLoadTimeoutReached}
        useChannelMessaging
        onChannelInit={handleChannelInit}
        onMessage={handleMessage}
        onPopState={handleBrowserBackButtonClicked}
        hashUrl="placement"
      />
    </PlacementDropdownContainer>
  );
};

export default PlacementDropdown;
